/**
 * Configuration-driven data for the "Agent-ready data" showcase experience.
 *
 * Every scenario follows the same beat, designed to be presented live:
 *   1. a business question is put to the agent          (chat)
 *   2. the agent returns two conflicting answers        (the hook)
 *   3. the presenter asks the audience for the cause    (audience prompt)
 *   4. the root cause(s) are revealed                   (the payoff)
 *
 * All copy lives here so the story can be edited without touching the UI.
 * Nav labels are deliberately neutral — they describe the question, never the
 * answer — so the audience can't read the root cause off the screen early.
 */

export type Tone = 'warning' | 'positive' | 'neutral';

/** The two assistants the same question is put to, side by side. */
export type AgentId = 'copilot' | 'mithra';

// The same assistant (Microsoft 365 Copilot) on both sides — the only difference
// is the data beneath it. The 'mithra' side is Copilot powered by Mithra data.
export const AGENTS: Record<AgentId, {name: string}> = {
    copilot: {name: 'Microsoft 365 Copilot'},
    mithra: {name: 'Microsoft 365 Copilot'},
};

/** One assistant's answer to the shared question. */
export type Answer = {
    /** Which assistant produced this answer */
    agent: AgentId;
    /** Headline figure shown in the chat */
    value: string;
    /** Short caption beneath the figure — identical across both answers */
    caption: string;
    /** Whether this answer is the correct, complete one */
    correct: boolean;
};

/** A revealed reason the answers diverged. The label alone tells the story. */
export type RootCause = {
    label: string;
};

/**
 * A minimal visual proof shown alongside the root cause: two data views side by
 * side, both Microsoft Copilot, one on bad (raw) data and one on good (Mithra)
 * data. The contrast is what produces the two different answers.
 *   'overlap'  : bad data sees one item valid in two branches; good data, one home
 *   'collapse' : bad data recognises only literal name matches and misses the rest;
 *                good data groups every variant under one normalised parent
 */
export type ScenarioExample =
    | {
        kind: 'overlap';
        /** Bad data: competing branches, the final level being the ambiguous leaf */
        copilotBranches: {levels: string[]}[];
        /** Good data: the single, canonical branch */
        atlasBranch: {levels: string[]};
    }
    | {
        kind: 'collapse';
        /** Used in the section titles: "Recognised as X" / "Not recognised as X" on
         *  the bad side, and "Grouped as X by Mithra" on the good side */
        recogniseAs: string;
        /** Every raw record; `matched` ones are the names bad data recognises */
        variants: {text: string; matched: boolean}[];
    };

export type Scenario = {
    id: string;
    index: string;
    /** Neutral nav label — names the question, never the root cause */
    navLabel: string;
    /** The question put to the agent */
    question: string;
    /** The two conflicting answers (wrong first, correct second) */
    answers: Answer[];
    /** Audience prompt shown before the reveal */
    prompt: string;
    /** Root cause(s) revealed last */
    rootCauses: RootCause[];
    /** Minimal worked example, revealed last */
    example: ScenarioExample;
};

// ---------------------------------------------------------------------------
// Hero / core message
// ---------------------------------------------------------------------------

export const HERO = {
    eyebrow: 'Procurement intelligence',
    title: 'Same question. Same agent. Two answers.',
    body: 'It’s the same Microsoft Copilot both times, asked the same question. The only difference is the data beneath it: raw on the left, prepared by Mithra on the right.',
};

// ---------------------------------------------------------------------------
// Scenarios
// ---------------------------------------------------------------------------

const scenarioMarketing: Scenario = {
    id: 'marketing',
    index: '01',
    navLabel: 'Marketing spend',
    question: 'How much did we spend on marketing in FY2026?',
    answers: [
        {agent: 'copilot', value: '€6.8M', caption: 'FY2026 marketing spend', correct: false},
        {agent: 'mithra', value: '€9.4M', caption: 'FY2026 marketing spend', correct: true},
    ],
    prompt: 'Same agent, same question, so why two different answers?',
    rootCauses: [
        {label: 'Overlapping taxonomy'},
        {label: 'Misclassified spend'},
    ],
    example: {
        kind: 'overlap',
        copilotBranches: [
            {levels: ['Marketing', 'Marketing Technology', 'Marketing Software']},
            {levels: ['IT', 'Software', 'SaaS Applications']},
        ],
        atlasBranch: {levels: ['Technology', 'Business Applications', 'Marketing Automation Software']},
    },
};

const scenarioMicrosoft: Scenario = {
    id: 'microsoft',
    index: '02',
    navLabel: 'Microsoft negotiation',
    question: 'What was our total FY2026 spend with Microsoft?',
    answers: [
        {agent: 'copilot', value: '€3.2M', caption: 'total Microsoft spend', correct: false},
        {agent: 'mithra', value: '€8.7M', caption: 'total Microsoft spend', correct: true},
    ],
    prompt: 'Same vendor, same year, so what do you think is hiding €5.5M?',
    rootCauses: [
        {label: 'Suppliers not normalized'},
    ],
    example: {
        kind: 'collapse',
        recogniseAs: 'Microsoft',
        variants: [
            {text: 'Microsoft Ireland Operations Ltd', matched: true},
            {text: 'MICROSOFT IRELAND', matched: true},
            {text: 'Microsoft Corporation', matched: true},
            {text: 'Microsoft BV', matched: true},
            {text: 'Microsoft EMEA', matched: true},
            {text: 'MICROSOFT 365', matched: true},
            {text: 'MSFT Azure', matched: false},
            {text: 'MS Corp.', matched: false},
        ],
    },
};

const scenarioMaterial: Scenario = {
    id: 'material',
    index: '03',
    navLabel: 'Pipe pricing',
    question: 'How many purchases do we have of 30 mm 316L stainless-steel pipe?',
    answers: [
        {agent: 'copilot', value: '2', caption: 'matching purchase records', correct: false},
        {agent: 'mithra', value: '25', caption: 'matching purchase records', correct: true},
    ],
    prompt: 'We buy this pipe across six plants, so why can the agent only find two?',
    rootCauses: [
        {label: 'Materials not harmonized'},
    ],
    example: {
        kind: 'collapse',
        recogniseAs: 'the same part',
        variants: [
            {text: 'SS PIPE 30MM', matched: true},
            {text: 'TUBE 316L 30X2', matched: true},
            {text: '316L PIPE DN30', matched: true},
            {text: 'PIPE INOX 3CM', matched: false},
            {text: 'ROHR EDELSTAHL 30X2', matched: false},
            {text: 'TUBO INOX 30 MM 316L', matched: false},
        ],
    },
};

export const SCENARIOS: Scenario[] = [
    scenarioMarketing,
    scenarioMicrosoft,
    scenarioMaterial,
];

// ---------------------------------------------------------------------------
// Final summary
// ---------------------------------------------------------------------------

export const SUMMARY = {
    eyebrow: 'Summary',
    title: 'Trusted agents require trusted data',
    foundations: [
        {
            index: '01',
            label: 'Highly optimised, benchmarked taxonomy',
            detail: 'Mutually exclusive categories, benchmarked against industry, so every transaction has exactly one home.',
        },
        {
            index: '02',
            label: 'Correctly classified spend',
            detail: 'Totals reflect every relevant transaction.',
        },
        {
            index: '03',
            label: 'Normalized supplier identities',
            detail: 'One vendor, one complete relationship.',
        },
        {
            index: '04',
            label: 'Harmonized material specifications',
            detail: 'Equivalent items compared across the enterprise.',
        },
    ],
    closing:
        'Agents don’t remove the need for data management. They make its quality consequential.',
    supporting:
        'Mithra keeps the taxonomy, suppliers, and materials beneath your agents clean, so the answers stay right.',
};
